// @ts-strict-ignore
import React, { useLayoutEffect, useMemo, useState } from 'react'

import {
	ITEM_SUBTYPE_SCREEN_STORY,
	ITEM_SUBTYPE_SCREEN_WEB,
	ITEM_SUBTYPE_SCREEN_PANORAMA,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_SUBTYPE_MENU,
	RootStoreState,
} from 'mytours-read-common'

import Page from '../Page'
import Modal from '../modal'
import classNames from 'classnames'

import './styles.less'
import { useSelector } from 'react-redux'
import { GetCollectionItemsIndexResponseData, GetScreensShowResponse } from 'mytours-read-api-typescript'
import { RouteComponentProps, withRouter } from 'react-router'
import CollectionListComponent from 'mobile_web/modules/collection/components/CollectionList'
import ScreenStoryComponent from 'mobile_web/modules/screen/components/ScreenStory'
import ScreenWebComponent from 'mobile_web/modules/screen/components/ScreenWeb'
import ScreenPanoramaComponent from 'mobile_web/modules/screen/components/ScreenPanorama'
import AudioPlayerBarComponent from 'mobile_web/modules/audio/components/AudioPlayerBar'
import CollectionTourComponent from 'mobile_web/modules/collection/components/CollectionTour'

interface Props {
	subtype: string
	itemId: number
	itemVersion?: number
	accountId?: number
	showModal: boolean
	loading?: boolean
	toggleModal: (showModal: boolean) => void
	className?: string
}

const getItemTypeFromSubtype = (subtype: string) => {
	switch (subtype) {
		case ITEM_SUBTYPE_SCREEN_STORY:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		case ITEM_SUBTYPE_SCREEN_WEB:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		case ITEM_SUBTYPE_SCREEN_PANORAMA:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		case COLLECTION_ITEM_SUBTYPE_WEB:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		case COLLECTION_ITEM_SUBTYPE_STORY:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		case COLLECTION_ITEM_SUBTYPE_PANORAMA:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		case COLLECTION_ITEM_SUBTYPE_LIST:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection
		case COLLECTION_ITEM_SUBTYPE_TOUR:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection
		case COLLECTION_ITEM_SUBTYPE_MENU:
			return GetCollectionItemsIndexResponseData.ItemTypeEnum.Collection
	}
}

const ModalPage: React.FC<Props & RouteComponentProps> = ({
	showModal,
	subtype,
	itemId,
	itemVersion,
	accountId,
	toggleModal,
	loading,
	className,
	location,
}): JSX.Element => {
	const [modalDidPresent, setModalDidPresent] = useState<boolean>(false)
	const [tabsHeight, setTabsHeight] = useState<number>(57)

	const screen = useSelector((state: RootStoreState) => state.screen.screens.get(itemId)?.screen)
	const collection = useSelector((state: RootStoreState) => state.collectionlist.collectionDetails.get(itemId)?.details)

	useLayoutEffect(() => {
		const waitForTabs = setInterval(() => {
			// use tabs height to set max height of the modal window
			const tabsHeightTmp = document.querySelector<HTMLElement>('.sq-tab-bar')?.offsetHeight
			if (tabsHeightTmp > 1) {
				clearInterval(waitForTabs)
				setTabsHeight(tabsHeightTmp)
			}
		}, 100)

		// The poll above only stops once it finds a tab bar with a height, so anywhere one never
		// appears it would otherwise outlive this component and keep touching the document.
		return () => clearInterval(waitForTabs)
	}, [])

	const title = useMemo<string>(() => {
		switch (subtype) {
			case COLLECTION_ITEM_SUBTYPE_STORY:
			case ITEM_SUBTYPE_SCREEN_STORY: {
				return screen?.header_layout === GetScreensShowResponse.HeaderLayoutEnum.ImageAndTitle
					? '' // hide header title duplication for screens with image and title header
					: screen?.title || ''
			}
			case COLLECTION_ITEM_SUBTYPE_WEB:
			case ITEM_SUBTYPE_SCREEN_WEB:
			case COLLECTION_ITEM_SUBTYPE_PANORAMA:
			case ITEM_SUBTYPE_SCREEN_PANORAMA: {
				return screen?.title || ''
			}
			case COLLECTION_ITEM_SUBTYPE_LIST:
			case COLLECTION_ITEM_SUBTYPE_MENU:
			case COLLECTION_ITEM_SUBTYPE_TOUR: {
				return collection?.title || ''
			}
			default:
				return ''
		}
	}, [screen?.title, collection?.title])

	const renderContent = () => {
		switch (subtype) {
			case COLLECTION_ITEM_SUBTYPE_STORY:
			case ITEM_SUBTYPE_SCREEN_STORY: {
				return (
					<ScreenStoryComponent
						loading={loading}
						itemId={itemId}
						itemVersion={itemVersion}
						accountId={accountId}
						isPageContent={true}
						closeModal={() => toggleModal(false)}
					/>
				)
			}
			case COLLECTION_ITEM_SUBTYPE_WEB:
			case ITEM_SUBTYPE_SCREEN_WEB: {
				return (
					<ScreenWebComponent
						itemId={itemId}
						itemVersion={itemVersion}
						accountId={accountId}
						isPageContent={true}
						loading={loading}
					/>
				)
			}
			case COLLECTION_ITEM_SUBTYPE_PANORAMA:
			case ITEM_SUBTYPE_SCREEN_PANORAMA: {
				return (
					<ScreenPanoramaComponent
						itemId={itemId}
						itemVersion={itemVersion}
						accountId={accountId}
						isPageContent={true}
						loading={loading}
					/>
				)
			}
			case COLLECTION_ITEM_SUBTYPE_LIST: {
				return <CollectionListComponent collectionId={itemId} collectionVersion={itemVersion} accountId={accountId} />
			}
			case COLLECTION_ITEM_SUBTYPE_TOUR: {
				return <CollectionTourComponent collectionId={itemId} collectionVersion={itemVersion} accountId={accountId} />
			}
			default:
				return null
		}
	}

	const { pathname } = location

	const styleHTML =
		// when a user opens modal from any tour page, then don't show tabs over modal
		!pathname.includes('/tour/') &&
		`
		.screen-modal {
			max-height: calc(100% - ${tabsHeight}px);
		}
	`

	return (
		<>
			<style dangerouslySetInnerHTML={{ __html: styleHTML }} />
			<Modal
				isOpen={showModal}
				cssClass={classNames('screen-modal', className)}
				showBackdrop={true}
				backdropDismiss={true}
				onDidDismiss={() => {
					toggleModal(false)
					setModalDidPresent(false)
				}}
				onDidPresent={() => {
					setModalDidPresent(true)
				}}
			>
				{modalDidPresent && (
					<Page
						headerProps={{
							title,
							showBackButton: true,
							onBackButton: () => toggleModal(false),
							type: 'short',
							loading,

							collection,
							screen,
							collectionItem: null,
							itemType: getItemTypeFromSubtype(subtype),
							itemId,
							hideFavouriteButton: false,
						}}
						footer={{
							component: <AudioPlayerBarComponent bottomOffset={0} />,
						}}
						dataCy="page-modal"
					>
						{renderContent()}
					</Page>
				)}
			</Modal>
		</>
	)
}

export default withRouter(ModalPage)
